/**
 * Atomistic geometry: sphere and cylinder instance lists for one (component, entry) pair, a port of
 * RenderingViewModel.Atomistic.cs (atoms 1011-1065, bonds 1158-1403, radius 1381-1390, plane vector 1453-1470,
 * aromatic offset 1392-1403). Pure; no GL.
 */
import type { VisualizationModel } from '@nanome-lite/state';
import type { NanomeModel } from '@nanome-lite/data';
import { BOND_LAYOUT, VDW_RADII, VDW_RADIUS_DEFAULT } from '@nanome-lite/look';

export interface SphereInstances {
  count: number;
  center: Float32Array;    // xyz Å (domain frame)
  radius: Float32Array;    // Å
  color: Float32Array;     // rgb 0..1 sRGB
  atomIndex: Uint32Array;  // model atom index
}

/** Cylinder flags (ImpostorCylinder.hlsl:10-12): bit 1 open start, bit 2 open end. */
export const CYL_OPEN_START = 2;
export const CYL_OPEN_END = 4;

export interface CylinderInstances {
  count: number;
  start: Float32Array;     // xyz Å
  end: Float32Array;       // xyz Å
  radius: Float32Array;    // Å
  colorStart: Float32Array;
  colorEnd: Float32Array;
  flags: Uint8Array;       // CYL_OPEN_*
  bondIndex: Uint32Array;  // model bond index
}

export interface AtomisticGeometry {
  spheres: SphereInstances;
  cylinders: CylinderInstances;
  /** atom indices actually drawn (after hydrogen / render-mode filtering), sorted */
  drawnAtoms: Uint32Array;
  /** sphere radius per drawn atom index (0 when no sphere), for highlight shells and picking */
  sphereRadiusOf: (atomIndex: number) => number;
}

/** vdW radius with the AtomicRadius.cs fallback (unknown element 2.0). */
export function vdwRadius(element: number): number {
  const r = VDW_RADII[element];
  return r === undefined || r === 0 ? VDW_RADIUS_DEFAULT : r;
}

/** GetRadius (RenderingViewModel.Atomistic.cs:1381-1390). NoSize is handled by the caller (no sphere). */
export function atomRadius(model: NanomeModel, atomIndex: number, viz: VisualizationModel, minB: number, maxB: number): number {
  const size = viz.size;
  switch (size.sizeOption) {
    case 'Uniform': return size.scale;
    case 'Physical': return vdwRadius(model.element[atomIndex]!) * size.scale;
    case 'Disorder': { const d = maxB - minB; return d > 0 ? (model.bfactor[atomIndex]! - minB) / d : 0; }
    default: return 0;
  }
}

/** Double bond layout (:1362-1369): scale = max(0.025, bondScale*minR*1.25), spacing = min(0.1, minR - scale). */
export function doubleBondLayout(bondScale: number, minRadius: number): { scale: number; space: number } {
  const scale = Math.max(BOND_LAYOUT.doubleMinSizing, bondScale * minRadius * 1.25);
  return { scale, space: Math.min(BOND_LAYOUT.doubleSpacing, minRadius - scale) };
}

/** Triple bond layout (:1371-1378): scale = max(0.015, bondScale*minR), spacing = min(0.2, minR - scale). */
export function tripleBondLayout(bondScale: number, minRadius: number): { scale: number; space: number } {
  const scale = Math.max(BOND_LAYOUT.tripleMinSizing, bondScale * minRadius);
  return { scale, space: Math.min(BOND_LAYOUT.tripleSpacing, minRadius - scale) };
}

type V3 = [number, number, number];
const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const mul = (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s];
const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const len = (a: V3) => Math.sqrt(dot(a, a));
const norm = (a: V3): V3 => { const l = len(a); return l > 1e-12 ? mul(a, 1 / l) : [0, 0, 0]; };
const pos = (m: NanomeModel, i: number): V3 => [m.xyz[i * 3]!, m.xyz[i * 3 + 1]!, m.xyz[i * 3 + 2]!];

/**
 * Perpendicular offset direction for double/triple bonds. Nanome builds LookRotationSafe(cross(up, plane), up) and
 * takes its x axis, which is normalize(cross(up, cross(up, plane))) = -(plane minus its component along up),
 * normalised; the sign cancels because offsets are symmetric (:1191-1197, 1275-1281). Identity when plane || up,
 * so x axis = (1, 0, 0) (quaternion.LookRotationSafe fallback).
 */
export function bondOffsetDirection(bondDir: V3, planeVector: V3): V3 {
  const up = norm(bondDir);
  const perp = sub(planeVector, mul(up, dot(planeVector, up)));
  const l = len(perp);
  if (l < 1e-6) return [1, 0, 0];
  return mul(perp, -1 / l);
}

/**
 * GetPlaneVector (:1453-1470): the first bond in list order touching atom1 or atom2 (not the bond itself) gives
 * normalize(main - other), with main/other chosen as the C# does. Default (0, 1, 0) in Unity space, which is
 * domain (0, 0, 1) after the y/z swap.
 */
export function planeVector(model: NanomeModel, bondsOfAtom: Int32Array[], atom1: number, atom2: number, bondIndex: number): V3 {
  const b = model.bonds;
  let best = -1;
  for (const list of [bondsOfAtom[atom1], bondsOfAtom[atom2]]) {
    if (!list) continue;
    for (const bi of list) {
      if (bi === bondIndex) continue;
      if (best < 0 || bi < best) best = bi;
    }
  }
  if (best < 0) return [0, 0, 1];
  const ba = b.a[best]!, bb = b.b[best]!;
  const main = ba === atom1 ? ba : bb;
  const other = ba === atom1 ? bb : ba;
  return norm(sub(pos(model, main), pos(model, other)));
}

/** Smallest ring through the bond (a, b), preferring aromatic bonds; null when none within 8 atoms. */
export function findRing(model: NanomeModel, bondsOfAtom: Int32Array[], a: number, b: number, bondIndex: number, aromaticOnly: boolean): number[] | null {
  const bonds = model.bonds;
  const prev = new Map<number, number>();
  const queue = [a];
  prev.set(a, -1);
  const depth = new Map<number, number>([[a, 0]]);
  while (queue.length) {
    const cur = queue.shift()!;
    const d = depth.get(cur)!;
    if (d >= 7) continue;
    const list = bondsOfAtom[cur];
    if (!list) continue;
    for (const bi of list) {
      if (bi === bondIndex) continue;
      if (aromaticOnly && !bonds.aromatic[bi]) continue;
      const nb = bonds.a[bi] === cur ? bonds.b[bi]! : bonds.a[bi]!;
      if (prev.has(nb)) continue;
      prev.set(nb, cur);
      depth.set(nb, d + 1);
      if (nb === b) {
        const ring: number[] = [];
        for (let x: number = b; x !== -1; x = prev.get(x)!) ring.push(x);
        return ring;
      }
      queue.push(nb);
    }
  }
  return null;
}

/** Aromatic inner-bar shift: normalize(ringCentroid - midpoint) * 0.35 Å (:1392-1403); zero without a ring. */
export function aromaticOffset(model: NanomeModel, ring: number[] | null, mid: V3): V3 {
  if (!ring) return [0, 0, 0];
  let c: V3 = [0, 0, 0];
  for (const i of ring) c = add(c, pos(model, i));
  c = mul(c, 1 / ring.length);
  return mul(norm(sub(c, mid)), BOND_LAYOUT.aromaticSpacing);
}

/** Per-atom bond index lists in bond-list order. */
export function bondsByAtom(model: NanomeModel): Int32Array[] {
  const counts = new Uint32Array(model.atomCount);
  const b = model.bonds;
  for (let i = 0; i < b.count; i++) { counts[b.a[i]!] = counts[b.a[i]!]! + 1; counts[b.b[i]!] = counts[b.b[i]!]! + 1; }
  const out: Int32Array[] = new Array(model.atomCount);
  for (let i = 0; i < model.atomCount; i++) out[i] = new Int32Array(counts[i]!);
  counts.fill(0);
  for (let i = 0; i < b.count; i++) {
    const x = b.a[i]!, y = b.b[i]!;
    out[x]![counts[x]!++] = i; out[y]![counts[y]!++] = i;
  }
  return out;
}

/**
 * Build sphere and cylinder instances for an Atomistic visualization over `atomIndices` (sorted) of `model`.
 * `colors` holds one rgb triple per entry of `atomIndices` (see coloring.ts).
 */
export function buildAtomistic(model: NanomeModel, atomIndices: Uint32Array, colors: Float32Array, viz: VisualizationModel, adjacency?: Int32Array[]): AtomisticGeometry {
  const renderAtom = viz.renderAtom ?? 'All';
  const renderBond = viz.renderBond ?? false;
  const bondScale = viz.bondScale ?? 0;
  const hydrogen = viz.renderHydrogen ?? 'All';
  const noSize = viz.size.sizeOption === 'NoSize';

  // Hydrogen filtering (1011-1030, 1052-1060): None drops every H; PolarOnly drops H bonded to C.
  const inSet = new Uint8Array(model.atomCount);
  const colorOf = new Map<number, number>(); // atomIndex -> offset into colors
  const kept: number[] = [];
  for (let k = 0; k < atomIndices.length; k++) {
    const i = atomIndices[k]!;
    const isH = model.element[i] === 1;
    if (isH && hydrogen === 'None') continue;
    if (isH && hydrogen === 'PolarOnly' && model.isNonPolarHydrogen[i]) continue;
    inSet[i] = 1; kept.push(i); colorOf.set(i, k * 3);
  }
  const drawnAtoms = Uint32Array.from(kept);

  // Disorder normalisation over the selection (minMaxDisorder)
  let minB = Infinity, maxB = -Infinity;
  if (viz.size.sizeOption === 'Disorder') for (const i of kept) { const v = model.bfactor[i]!; if (v < minB) minB = v; if (v > maxB) maxB = v; }

  const radiusOf = (i: number) => (noSize ? 0 : atomRadius(model, i, viz, minB, maxB));
  // GetRenderedAtomRadius (:1343-1352): 0 when None, NonBondedOnly with bonds, or NoSize
  const sphereRadiusOf = (i: number) => {
    if (renderAtom === 'None' || noSize) return 0;
    if (renderAtom === 'NonBondedOnly' && model.bondCount[i]! > 0) return 0;
    return radiusOf(i);
  };

  // Spheres
  const sCenter: number[] = [], sRadius: number[] = [], sColor: number[] = [], sAtom: number[] = [];
  if (renderAtom !== 'None' && !noSize) {
    for (const i of kept) {
      if (renderAtom === 'NonBondedOnly' && model.bondCount[i]! > 0) continue;
      const c = colorOf.get(i)!;
      sCenter.push(model.xyz[i * 3]!, model.xyz[i * 3 + 1]!, model.xyz[i * 3 + 2]!);
      sRadius.push(radiusOf(i));
      sColor.push(colors[c]!, colors[c + 1]!, colors[c + 2]!);
      sAtom.push(i);
    }
  }

  // Bonds: both atoms drawn and renderBond
  const cStart: number[] = [], cEnd: number[] = [], cRadius: number[] = [], cCs: number[] = [], cCe: number[] = [], cFlags: number[] = [], cBond: number[] = [];
  if (renderBond && bondScale > 0) {
    const adj = adjacency ?? bondsByAtom(model);
    const b = model.bonds;
    // GetBondLayoutRadius (:1355-1358): NoSize -> bondScale else the atom radius
    const layoutRadius = (i: number) => (noSize ? bondScale : radiusOf(i));
    // A sphere covering a cylinder end hides its cap: draw that end open (:1158-1160)
    const hidden = (sphereR: number, offset: number, cylR: number) => sphereR >= offset + cylR;
    const push = (s: V3, e: V3, r: number, ca: number, cb: number, flags: number, bi: number) => {
      cStart.push(s[0], s[1], s[2]); cEnd.push(e[0], e[1], e[2]); cRadius.push(r);
      cCs.push(colors[ca]!, colors[ca + 1]!, colors[ca + 2]!); cCe.push(colors[cb]!, colors[cb + 1]!, colors[cb + 2]!);
      cFlags.push(flags); cBond.push(bi);
    };
    for (let bi = 0; bi < b.count; bi++) {
      const a1 = b.a[bi]!, a2 = b.b[bi]!;
      if (!inSet[a1] || !inSet[a2]) continue;
      const ca = colorOf.get(a1)!, cb = colorOf.get(a2)!;
      const start = pos(model, a1), end = pos(model, a2);
      const order = b.order[bi]!;
      const aromatic = b.aromatic[bi] === 1;
      const sr1 = sphereRadiusOf(a1), sr2 = sphereRadiusOf(a2);
      if (order === 2 && aromatic) {
        // full cylinder + inner bar at 0.7x, inset 0.4 Å, shifted 0.35 Å toward the ring centroid (:1229-1263)
        const flags = (hidden(sr1, 0, bondScale) ? CYL_OPEN_START : 0) | (hidden(sr2, 0, bondScale) ? CYL_OPEN_END : 0);
        push(start, end, bondScale, ca, cb, flags, bi);
        const dir = norm(sub(end, start));
        const mid = mul(add(start, end), 0.5);
        const ring = findRing(model, adj, a1, a2, bi, true) ?? findRing(model, adj, a1, a2, bi, false);
        const off = aromaticOffset(model, ring, mid);
        push(add(add(start, mul(dir, BOND_LAYOUT.aromaticOffset)), off), add(sub(end, mul(dir, BOND_LAYOUT.aromaticOffset)), off), bondScale * BOND_LAYOUT.aromaticSizing, ca, cb, 0, bi);
      } else if (order === 2) {
        const minR = Math.min(layoutRadius(a1), layoutRadius(a2));
        const { scale, space } = doubleBondLayout(bondScale, minR);
        const right = mul(bondOffsetDirection(sub(end, start), planeVector(model, adj, a1, a2, bi)), space);
        const flags = (hidden(sr1, space, scale) ? CYL_OPEN_START : 0) | (hidden(sr2, space, scale) ? CYL_OPEN_END : 0);
        push(add(start, right), add(end, right), scale, ca, cb, flags, bi);
        push(sub(start, right), sub(end, right), scale, ca, cb, flags, bi);
      } else if (order === 3) {
        const minR = Math.min(layoutRadius(a1), layoutRadius(a2));
        const { scale, space } = tripleBondLayout(bondScale, minR);
        const right = mul(bondOffsetDirection(sub(end, start), planeVector(model, adj, a1, a2, bi)), space);
        const flags = (hidden(sr1, space, scale) ? CYL_OPEN_START : 0) | (hidden(sr2, space, scale) ? CYL_OPEN_END : 0);
        push(start, end, scale, ca, cb, flags, bi);
        push(add(start, right), add(end, right), scale, ca, cb, flags, bi);
        push(sub(start, right), sub(end, right), scale, ca, cb, flags, bi);
      } else {
        const flags = (hidden(sr1, 0, bondScale) ? CYL_OPEN_START : 0) | (hidden(sr2, 0, bondScale) ? CYL_OPEN_END : 0);
        push(start, end, bondScale, ca, cb, flags, bi);
      }
    }
  }

  return {
    spheres: { count: sAtom.length, center: Float32Array.from(sCenter), radius: Float32Array.from(sRadius), color: Float32Array.from(sColor), atomIndex: Uint32Array.from(sAtom) },
    cylinders: { count: cBond.length, start: Float32Array.from(cStart), end: Float32Array.from(cEnd), radius: Float32Array.from(cRadius), colorStart: Float32Array.from(cCs), colorEnd: Float32Array.from(cCe), flags: Uint8Array.from(cFlags), bondIndex: Uint32Array.from(cBond) },
    drawnAtoms,
    sphereRadiusOf,
  };
}
